const cloneConfig = (config) => ({ ...(config || {}) });

const getValue = (section, snakeKey, camelKey, pascalKey) => (
  section?.[snakeKey] ?? section?.[camelKey] ?? section?.[pascalKey]
);

const cloneSection = (section) => {
  const clone = {
    ...section,
    id: section?.id ?? section?.Id,
    section_type: getValue(section, 'section_type', 'sectionType', 'SectionType'),
    section_order: getValue(section, 'section_order', 'sectionOrder', 'SectionOrder'),
    is_visible: getValue(section, 'is_visible', 'isVisible', 'IsVisible'),
    config: cloneConfig(section?.config ?? section?.Config),
  };
  const pageName = getValue(section, 'page_name', 'pageName', 'PageName');
  if (pageName !== undefined) clone.page_name = pageName;
  return clone;
};

const getOrder = (section) => {
  const order = Number(section?.section_order);
  return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
};

const isRemoved = (section) => section?.config?.__removed === true;

const hasConfigValue = (section) => section?.config && Object.keys(section.config).length > 0;

const clearRemovedMarker = (config = {}) => {
  const { __removed, ...rest } = config || {};
  return rest;
};

const choosePreferredSection = (current, candidate) => {
  if (!current) return candidate;

  const currentRemoved = isRemoved(current);
  const candidateRemoved = isRemoved(candidate);
  if (currentRemoved !== candidateRemoved) {
    return candidateRemoved ? current : candidate;
  }

  const currentVisible = current.is_visible !== false;
  const candidateVisible = candidate.is_visible !== false;
  if (currentVisible !== candidateVisible) {
    return candidateVisible ? candidate : current;
  }

  const currentHasConfig = hasConfigValue(current);
  const candidateHasConfig = hasConfigValue(candidate);
  if (currentHasConfig !== candidateHasConfig) {
    return candidateHasConfig ? candidate : current;
  }

  const currentOrder = getOrder(current);
  const candidateOrder = getOrder(candidate);
  if (candidateOrder !== currentOrder) {
    return candidateOrder < currentOrder ? candidate : current;
  }

  return current;
};

const dedupeSectionsByType = (sections) => {
  const grouped = new Map();

  for (const section of sections || []) {
    if (!section?.section_type) continue;
    const key = section.section_type;
    const clone = cloneSection(section);
    const group = grouped.get(key) || { items: [], preferred: null };
    group.items.push(clone);
    group.preferred = choosePreferredSection(group.preferred, clone);
    grouped.set(key, group);
  }

  return Array.from(grouped.values()).map(({ items, preferred }) => {
    const mergedConfig = items
      .slice()
      .sort((a, b) => getOrder(a) - getOrder(b))
      .reduce((config, item) => ({ ...config, ...cloneConfig(item.config) }), {});
    const preferredConfig = {
      ...mergedConfig,
      ...cloneConfig(preferred.config),
    };

    return {
      ...preferred,
      config: isRemoved(preferred) ? preferredConfig : clearRemovedMarker(preferredConfig),
    };
  });
};

export const normalizeSectionOrder = (sections) => (
  [...(sections || [])]
    .sort((a, b) => getOrder(a) - getOrder(b))
    .map((section, index) => ({ ...section, section_order: index + 1 }))
);

/**
 * Section types that no longer exist. Saved rows of these types are dropped at
 * every merge boundary so legacy published configs cannot render them.
 */
const RETIRED_SECTION_TYPES = new Set(['hero_partners']);

const isRetired = (section) => RETIRED_SECTION_TYPES.has(
  getValue(section, 'section_type', 'sectionType', 'SectionType')
);

/**
 * Merge fetched sections with defaults while enforcing one row per section_type.
 * This is used at every cache/API/draft/save boundary so stale duplicate rows
 * cannot leak back into render state or publish payloads.
 */
export const mergeSectionsWithDefaults = (fetched, defaults = [], fallback = null, options = {}) => {
  const defaultSections = (defaults || []).filter(section => !isRetired(section)).map(cloneSection);
  if (!fetched || fetched.length === 0) {
    return options.renumber ? normalizeSectionOrder(defaultSections) : defaultSections;
  }

  const normalizedFetched = (fetched || []).filter(section => !isRetired(section)).map(cloneSection);
  const normalizedFallback = (fallback || []).filter(Boolean).map(cloneSection);
  const defaultMap = new Map(defaultSections.map(section => [section.section_type, section]));
  const fetchedTypes = new Set(normalizedFetched.map(section => section?.section_type).filter(Boolean));
  const fallbackMap = new Map(normalizedFallback.map(section => [section.section_type, section]));

  const removedTypes = new Set(
    normalizedFetched
      .filter(section => section?.config?.__removed === true)
      .map(section => section.section_type)
  );

  let merged = normalizedFetched
    .filter(section => section?.section_type)
    .map(section => {
      const def = defaultMap.get(section.section_type);
      const fb = fallbackMap.get(section.section_type);
      const mergedConfig = {
        ...cloneConfig(def?.config),
        ...cloneConfig(fb?.config),
        ...cloneConfig(section.config),
      };

      return def
        ? {
            ...def,
            is_visible: section.is_visible,
            section_order: section.section_order,
            id: section.id,
            config: mergedConfig,
          }
        : { ...cloneSection(section), config: mergedConfig };
    });

  const newSections = defaultSections.filter(
    section => !fetchedTypes.has(section.section_type) && !removedTypes.has(section.section_type)
  );

  const sortedNew = [...newSections].sort((a, b) => getOrder(a) - getOrder(b));
  for (const section of sortedNew) {
    const insertOrder = Number.isFinite(Number(section.section_order))
      ? Number(section.section_order)
      : merged.reduce((max, item) => Math.max(max, getOrder(item)), 0) + 1;

    merged = merged.map(item => (
      getOrder(item) >= insertOrder
        ? { ...item, section_order: getOrder(item) + 1 }
        : item
    ));
    merged.push({ ...section, section_order: insertOrder });
  }

  const normalized = dedupeSectionsByType(merged).sort((a, b) => getOrder(a) - getOrder(b));
  return options.renumber ? normalizeSectionOrder(normalized) : normalized;
};

export const normalizeSectionsForSave = (sections, defaults = []) => (
  mergeSectionsWithDefaults(sections, defaults, null, { renumber: true })
);

export const getCanonicalSectionType = (sections, sectionId, explicitSectionType = null) => {
  if (explicitSectionType) return explicitSectionType;
  const byId = (sections || []).find(section => String(section?.id) === String(sectionId));
  if (byId?.section_type) return byId.section_type;
  const byType = (sections || []).find(section => section?.section_type === sectionId);
  return byType?.section_type || null;
};

export const applySectionMutation = (sections, defaults = [], pageName, sectionId, updates, options = {}) => {
  let nextSections = mergeSectionsWithDefaults(sections, defaults);

  if (updates?.reorderAll) {
    return mergeSectionsWithDefaults(updates.reorderAll, defaults);
  }

  if (sectionId === null) {
    const sectionType = updates?.section_type;
    const existingIndex = sectionType
      ? nextSections.findIndex(section => section.section_type === sectionType)
      : -1;

    if (existingIndex >= 0) {
      const existing = nextSections[existingIndex];
      const nextVisible = updates.is_visible ?? true;
      nextSections[existingIndex] = {
        ...existing,
        ...updates,
        id: existing.id,
        page_name: existing.page_name || pageName,
        section_type: existing.section_type,
        is_visible: nextVisible,
        config: {
          ...(nextVisible ? clearRemovedMarker(existing.config) : existing.config || {}),
          ...(updates.config || {}),
        },
      };
    } else {
      nextSections.push({
        id: Date.now(),
        page_name: pageName,
        ...updates,
      });
    }

    return mergeSectionsWithDefaults(nextSections, defaults);
  }

  const sectionType = getCanonicalSectionType(nextSections, sectionId, options.sectionType);
  const targetIndex = sectionType
    ? nextSections.findIndex(section => section.section_type === sectionType)
    : nextSections.findIndex(section => String(section.id) === String(sectionId));

  if (targetIndex < 0) {
    if (options.sectionType && updates !== null) {
      nextSections.push({
        id: sectionId || Date.now(),
        page_name: pageName,
        section_type: options.sectionType,
        section_order: nextSections.length + 1,
        is_visible: updates?.is_visible ?? true,
        config: updates?.config || {},
      });
      return mergeSectionsWithDefaults(nextSections, defaults);
    }
    return nextSections;
  }

  const target = nextSections[targetIndex];

  if (updates === null) {
    const isDefaultType = defaults.some(section => section.section_type === target.section_type);

    if (isDefaultType) {
      nextSections[targetIndex] = {
        ...target,
        is_visible: false,
        config: { ...(target.config || {}), __removed: true },
      };
    } else {
      nextSections = nextSections.filter(section => section.section_type !== target.section_type);
    }

    return mergeSectionsWithDefaults(nextSections, defaults);
  }

  const nextVisible = updates.is_visible ?? target.is_visible;
  nextSections[targetIndex] = {
    ...target,
    ...updates,
    id: target.id,
    page_name: target.page_name || pageName,
    section_type: target.section_type,
    config: updates.config !== undefined
      ? updates.config
      : (nextVisible ? clearRemovedMarker(target.config) : target.config || {}),
  };

  if (nextVisible && nextSections[targetIndex].config?.__removed) {
    nextSections[targetIndex] = {
      ...nextSections[targetIndex],
      config: clearRemovedMarker(nextSections[targetIndex].config),
    };
  }

  return mergeSectionsWithDefaults(nextSections, defaults);
};